import { Controller } from "@hotwired/stimulus"

// 모달 컨트롤러 - Design System v3
// 모달 열기/닫기, 포커스 트랩, 중첩 모달 지원, 드래그 이동
// 주의: ESC 키나 배경 클릭으로 닫히지 않음 (버튼 클릭만 가능)
export default class extends Controller {
  static targets = ["container", "content", "body", "footer", "confirmButton", "backdrop", "header"]

  // 열린 모달 스택 관리 (중첩 모달 지원)
  static openModals = []
  static baseZIndex = 50

  connect() {
    this.handleFocusTrap = this.handleFocusTrap.bind(this)

    // 드래그 관련 바인딩
    this.handleDragStart = this.handleDragStart.bind(this)
    this.handleDrag = this.handleDrag.bind(this)
    this.handleDragEnd = this.handleDragEnd.bind(this)

    // 커스텀 이벤트로 모달 열기 지원
    this.element.addEventListener('modal:open', () => this.open())

    // 드래그 상태 초기화
    this.isDragging = false
    this.dragStartX = 0
    this.dragStartY = 0
    this.modalStartX = 0
    this.modalStartY = 0
  }

  disconnect() {
    this.removeFromStack()
    this.removeEventListeners()
    this.restoreBodyScroll()
  }

  open(event) {
    if (event) event.preventDefault()

    // 스택에 추가하고 z-index 설정
    this.addToStack()

    // 모달 위치 초기화 (중앙)
    this.resetModalPosition()

    this.containerTarget.classList.remove("hidden")
    this.preventBodyScroll()
    this.addEventListeners()

    // hidden 해제 후 스크롤 리셋 (focusFirstElement 이후 다시 리셋)
    this.scrollToTop()
    this.focusFirstElement()
    // 포커스에 의한 자동 스크롤을 되돌리기 위해 다시 리셋
    requestAnimationFrame(() => this.scrollToTop())
  }

  resetModalPosition() {
    this.contentTarget.style.position = ''
    this.contentTarget.style.width = ''
    this.contentTarget.style.transform = ''
    this.contentTarget.style.left = ''
    this.contentTarget.style.top = ''
    this.contentTarget.style.margin = ''
  }

  close(event) {
    if (event) event.preventDefault()

    this.containerTarget.classList.add("hidden")
    this.removeFromStack()
    this.removeEventListeners()

    // 다른 열린 모달이 없을 때만 스크롤 복원
    if (this.constructor.openModals.length === 0) {
      this.restoreBodyScroll()
    }
  }

  confirm(event) {
    // Dispatch custom event for confirmation
    this.dispatch("confirm", { detail: { modal: this } })

    // Default behavior: close modal
    // Can be prevented by event listener
    if (!event.defaultPrevented) {
      this.close(event)
    }
  }

  // Stack management for nested modals

  addToStack() {
    const stack = this.constructor.openModals
    const zIndex = this.constructor.baseZIndex + (stack.length * 10)

    // z-index 설정
    this.containerTarget.style.zIndex = zIndex

    // 스택에 추가
    stack.push(this)
  }

  removeFromStack() {
    const stack = this.constructor.openModals
    const index = stack.indexOf(this)
    if (index > -1) {
      stack.splice(index, 1)
    }

    // z-index 초기화
    this.containerTarget.style.zIndex = ''
  }

  isTopModal() {
    const stack = this.constructor.openModals
    return stack.length > 0 && stack[stack.length - 1] === this
  }

  // Private methods

  handleFocusTrap(event) {
    if (event.key !== "Tab") return
    if (!this.isTopModal()) return

    const focusableElements = this.contentTarget.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault()
        lastElement?.focus()
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault()
        firstElement?.focus()
      }
    }
  }

  focusFirstElement() {
    // Focus confirm button by default if available
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.focus({ preventScroll: true })
      return
    }

    // footer가 없는 모달은 포커스를 설정하지 않음 (스크롤 방지)
    // footer가 있는 모달은 마지막 버튼에 포커스
    if (this.hasFooterTarget) {
      const focusableElements = this.contentTarget.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const lastElement = focusableElements[focusableElements.length - 1]
      lastElement?.focus({ preventScroll: true })
    }
  }

  addEventListeners() {
    document.addEventListener("keydown", this.handleFocusTrap)

    // 드래그 이벤트 (헤더가 있을 때만)
    if (this.hasHeaderTarget) {
      this.headerTarget.addEventListener("mousedown", this.handleDragStart)
      this.headerTarget.style.cursor = "move"
    }
  }

  removeEventListeners() {
    document.removeEventListener("keydown", this.handleFocusTrap)

    // 드래그 이벤트 제거
    if (this.hasHeaderTarget) {
      this.headerTarget.removeEventListener("mousedown", this.handleDragStart)
    }
    document.removeEventListener("mousemove", this.handleDrag)
    document.removeEventListener("mouseup", this.handleDragEnd)
  }

  // 드래그 시작
  handleDragStart(event) {
    // 버튼이나 input 등은 드래그 제외
    if (event.target.closest('button, input, select, textarea, a')) return

    this.isDragging = true
    this.dragStartX = event.clientX
    this.dragStartY = event.clientY

    // 현재 모달 위치와 크기 가져오기
    const rect = this.contentTarget.getBoundingClientRect()
    this.modalStartX = rect.left
    this.modalStartY = rect.top

    // 모달을 fixed 위치로 전환 (현재 크기 유지)
    this.contentTarget.style.position = 'fixed'
    this.contentTarget.style.width = `${rect.width}px`
    this.contentTarget.style.left = `${this.modalStartX}px`
    this.contentTarget.style.top = `${this.modalStartY}px`
    this.contentTarget.style.transform = 'none'
    this.contentTarget.style.margin = '0'

    document.addEventListener("mousemove", this.handleDrag)
    document.addEventListener("mouseup", this.handleDragEnd)

    // 텍스트 선택 방지
    event.preventDefault()
  }

  // 드래그 중
  handleDrag(event) {
    if (!this.isDragging) return

    const deltaX = event.clientX - this.dragStartX
    const deltaY = event.clientY - this.dragStartY

    const newX = this.modalStartX + deltaX
    const newY = this.modalStartY + deltaY

    this.contentTarget.style.left = `${newX}px`
    this.contentTarget.style.top = `${newY}px`
  }

  // 드래그 종료
  handleDragEnd() {
    this.isDragging = false
    document.removeEventListener("mousemove", this.handleDrag)
    document.removeEventListener("mouseup", this.handleDragEnd)
  }

  scrollToTop() {
    // 모달 내 모든 스크롤 가능 영역을 맨 위로 리셋
    this.containerTarget.scrollTop = 0
    this.contentTarget.scrollTop = 0
    if (this.hasBodyTarget) {
      this.bodyTarget.scrollTop = 0
    }
    // 모달 내부의 overflow-auto/overflow-y-auto 요소도 리셋
    this.containerTarget.querySelectorAll('[class*="overflow"]').forEach(el => {
      el.scrollTop = 0
    })
  }

  preventBodyScroll() {
    // 첫 번째 모달이 열릴 때만 원래 상태 저장
    if (this.constructor.openModals.length === 1) {
      this.constructor.originalOverflow = document.body.style.overflow
    }
    document.body.style.overflow = "hidden"
  }

  restoreBodyScroll() {
    document.body.style.overflow = this.constructor.originalOverflow || ""
  }
}
